/**
 * <s-product-image-zoom> — PDP gallery enhancements.
 *
 * - Warms the high-resolution zoom image on hover/focus of a gallery trigger.
 * - Inside each fullscreen zoom dialog: tap/click to zoom, mouse-wheel zoom,
 *   pinch-to-zoom, drag to pan, +/- buttons, keyboard (+, -, 0, arrows) and
 *   previous/next navigation between images.
 * - Updates the mobile "1 / N" counter as the gallery is swiped.
 *
 * Required markup:
 *   [data-zoom-preload][commandfor]      — gallery trigger; commandfor points to a dialog id.
 *   dialog [data-zoom-stage] img[data-zoom-image] — zoomable image inside the dialog.
 *   dialog [data-zoom-in|out|prev|next], [data-zoom-level] — optional controls.
 */
const MIN_SCALE = 1;
const MAX_SCALE = 4;
const TAP_SCALE = 2.5;
const STEP = 1.5;

class ZoomController {
  scale = 1;
  x = 0;
  y = 0;
  /** @type {Map<number, {x: number, y: number}>} */
  pointers = new Map();
  pinchStart = 0;
  pinchScale = 1;
  dragStart = null;
  moved = false;

  /** @param {HTMLDialogElement} dialog @param {(dir: number) => void} navigate @param {AbortSignal} signal */
  constructor(dialog, navigate, signal) {
    this.dialog = dialog;
    this.stage = /** @type {HTMLElement} */ (dialog.querySelector('[data-zoom-stage]'));
    this.image = /** @type {HTMLImageElement} */ (dialog.querySelector('img[data-zoom-image]'));
    this.level = dialog.querySelector('[data-zoom-level]');
    this.zoomIn = /** @type {HTMLButtonElement | null} */ (dialog.querySelector('[data-zoom-in]'));
    this.zoomOut = /** @type {HTMLButtonElement | null} */ (dialog.querySelector('[data-zoom-out]'));
    if (!this.stage || !this.image) return;

    const opts = { signal };
    this.zoomIn?.addEventListener('click', () => this.zoomAt(this.scale * STEP), opts);
    this.zoomOut?.addEventListener('click', () => this.zoomAt(this.scale / STEP), opts);
    dialog.querySelector('[data-zoom-prev]')?.addEventListener('click', () => navigate(-1), opts);
    dialog.querySelector('[data-zoom-next]')?.addEventListener('click', () => navigate(1), opts);

    this.stage.addEventListener('wheel', this.onWheel, { passive: false, signal });
    this.stage.addEventListener('pointerdown', this.onPointerDown, opts);
    this.stage.addEventListener('pointermove', this.onPointerMove, opts);
    this.stage.addEventListener('pointerup', this.onPointerUp, opts);
    this.stage.addEventListener('pointercancel', this.onPointerUp, opts);
    dialog.addEventListener('keydown', (event) => this.onKey(event, navigate), opts);
    dialog.addEventListener('close', () => this.reset(), opts);
    this.render();
  }

  reset() {
    this.scale = 1;
    this.x = 0;
    this.y = 0;
    this.pointers.clear();
    this.render();
  }

  /** Point relative to the stage centre. */
  offset(clientX, clientY) {
    const rect = this.stage.getBoundingClientRect();
    return { dx: clientX - (rect.left + rect.width / 2), dy: clientY - (rect.top + rect.height / 2) };
  }

  /** Zoom to `next`, keeping the given stage-centre offset under the pointer. */
  zoomAt(next, dx = 0, dy = 0) {
    const target = Math.min(MAX_SCALE, Math.max(MIN_SCALE, next));
    const ratio = target / this.scale;
    this.x = dx - (dx - this.x) * ratio;
    this.y = dy - (dy - this.y) * ratio;
    this.scale = target;
    if (this.scale === 1) {
      this.x = 0;
      this.y = 0;
    }
    this.render();
  }

  clamp() {
    const rect = this.stage.getBoundingClientRect();
    const maxX = (rect.width * (this.scale - 1)) / 2;
    const maxY = (rect.height * (this.scale - 1)) / 2;
    this.x = Math.min(maxX, Math.max(-maxX, this.x));
    this.y = Math.min(maxY, Math.max(-maxY, this.y));
  }

  render() {
    this.clamp();
    this.image.style.transform = `translate3d(${this.x}px, ${this.y}px, 0) scale(${this.scale})`;
    const zoomed = this.scale > 1;
    this.stage.toggleAttribute('data-zoomed', zoomed);
    if (this.level) this.level.textContent = `${Math.round(this.scale * 100)}%`;
    if (this.zoomOut) this.zoomOut.disabled = this.scale <= MIN_SCALE;
    if (this.zoomIn) this.zoomIn.disabled = this.scale >= MAX_SCALE;
  }

  /** @param {WheelEvent} event */
  onWheel = (event) => {
    event.preventDefault();
    const { dx, dy } = this.offset(event.clientX, event.clientY);
    this.zoomAt(this.scale * Math.exp(-event.deltaY * 0.0025), dx, dy);
  };

  /** @param {PointerEvent} event */
  onPointerDown = (event) => {
    this.stage.setPointerCapture(event.pointerId);
    this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
    this.moved = false;
    if (this.pointers.size === 2) {
      const [a, b] = [...this.pointers.values()];
      this.pinchStart = Math.hypot(a.x - b.x, a.y - b.y);
      this.pinchScale = this.scale;
    } else {
      this.dragStart = { x: event.clientX - this.x, y: event.clientY - this.y, cx: event.clientX, cy: event.clientY };
    }
  };

  /** @param {PointerEvent} event */
  onPointerMove = (event) => {
    if (!this.pointers.has(event.pointerId)) return;
    this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });

    if (this.pointers.size === 2 && this.pinchStart > 0) {
      const [a, b] = [...this.pointers.values()];
      const distance = Math.hypot(a.x - b.x, a.y - b.y);
      const { dx, dy } = this.offset((a.x + b.x) / 2, (a.y + b.y) / 2);
      this.moved = true;
      this.stage.setAttribute('data-dragging', '');
      this.image.setAttribute('data-dragging', '');
      this.zoomAt(this.pinchScale * (distance / this.pinchStart), dx, dy);
      return;
    }

    if (this.dragStart && Math.hypot(event.clientX - this.dragStart.cx, event.clientY - this.dragStart.cy) > 6) {
      this.moved = true;
      if (this.scale > 1) {
        this.stage.setAttribute('data-dragging', '');
        this.image.setAttribute('data-dragging', '');
        this.x = event.clientX - this.dragStart.x;
        this.y = event.clientY - this.dragStart.y;
        this.render();
      }
    }
  };

  /** @param {PointerEvent} event */
  onPointerUp = (event) => {
    const wasTap = !this.moved && this.pointers.size === 1;
    this.pointers.delete(event.pointerId);
    if (this.pointers.size < 2) this.pinchStart = 0;
    if (this.pointers.size === 0) {
      this.dragStart = null;
      this.stage.removeAttribute('data-dragging');
      this.image.removeAttribute('data-dragging');
    }
    if (wasTap && event.type === 'pointerup') {
      if (this.scale > 1) {
        this.zoomAt(1);
      } else {
        const { dx, dy } = this.offset(event.clientX, event.clientY);
        this.zoomAt(TAP_SCALE, dx, dy);
      }
    }
  };

  /** @param {KeyboardEvent} event @param {(dir: number) => void} navigate */
  onKey(event, navigate) {
    if (event.key === '+' || event.key === '=') {
      event.preventDefault();
      this.zoomAt(this.scale * STEP);
    } else if (event.key === '-' || event.key === '_') {
      event.preventDefault();
      this.zoomAt(this.scale / STEP);
    } else if (event.key === '0') {
      event.preventDefault();
      this.zoomAt(1);
    } else if (this.scale === 1 && (event.key === 'ArrowRight' || event.key === 'ArrowLeft')) {
      event.preventDefault();
      const rtl = getComputedStyle(this.dialog).direction === 'rtl';
      const forward = event.key === 'ArrowRight' ? 1 : -1;
      navigate(rtl ? -forward : forward);
    }
  }
}

class SProductImageZoom extends HTMLElement {
  #ac;
  #preloaded = new Set();
  #preloadLinks = [];

  connectedCallback() {
    this.#ac = new AbortController();
    const signal = this.#ac.signal;
    const opts = { signal };

    this.addEventListener('pointerover', this.#handlePreloadIntent, opts);
    this.addEventListener('focusin', this.#handlePreloadIntent, opts);

    const dialogs = /** @type {HTMLDialogElement[]} */ ([...this.querySelectorAll('dialog')].filter((d) => d.querySelector('[data-zoom-stage]')));
    const navigate = (from) => (dir) => {
      const index = dialogs.indexOf(from);
      const next = dialogs[(index + dir + dialogs.length) % dialogs.length];
      if (!next || next === from) return;
      this.#preloadDialog(next);
      from.close();
      next.showModal();
    };
    for (const dialog of dialogs) new ZoomController(dialog, navigate(dialog), signal);

    this.#setupCounter(signal);
    this.#setupVariantMedia(signal);
  }

  /** Brings the selected variant's image to the front of the gallery. @param {AbortSignal} signal */
  #setupVariantMedia(signal) {
    const gallery = this.querySelector('[data-product-gallery]');
    const mapScript = this.querySelector('script[data-variant-media]');
    if (!(gallery instanceof HTMLElement) || !mapScript) return;

    /** @type {Record<string, string>} */
    let variantMedia = {};
    try {
      variantMedia = JSON.parse(mapScript.textContent || '{}');
    } catch {
      return;
    }

    const spanClass = 'md:col-span-2';
    const spansFirst = gallery.querySelector(`:scope > .${CSS.escape(spanClass)}`) !== null;

    const show = (variantId, { scroll }) => {
      const mediaId = variantMedia[String(variantId)];
      if (!mediaId) return;
      const item = gallery.querySelector(`:scope > [data-media-id="${CSS.escape(mediaId)}"]`);
      if (!(item instanceof HTMLElement)) return;

      const changed = gallery.firstElementChild !== item;
      if (changed) {
        gallery.prepend(item);
        if (spansFirst) {
          for (const child of gallery.children) child.classList.remove(spanClass);
          item.classList.add(spanClass);
        }
      }

      gallery.scrollTo({ left: 0, behavior: scroll ? 'smooth' : 'auto' });
      const current = this.querySelector('[data-gallery-current]');
      if (current) current.textContent = '1';

      // Only bring the gallery back into view when the photo actually changed.
      if (scroll && changed) {
        const top = gallery.getBoundingClientRect().top;
        if (top < 0) {
          const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
          gallery.scrollIntoView({ block: 'start', behavior: reduce ? 'auto' : 'smooth' });
        }
      }
    };

    const initial = gallery.dataset.initialVariant;
    if (initial) show(initial, { scroll: false });

    document.addEventListener(
      'theme:variant-change',
      (event) => {
        const detail = /** @type {CustomEvent} */ (event).detail;
        const variantId = detail?.variantId;
        if (variantId) show(variantId, { scroll: detail?.scroll !== false });
      },
      { signal }
    );
  }

  disconnectedCallback() {
    this.#ac?.abort();
    for (const link of this.#preloadLinks) link.remove();
    this.#preloadLinks = [];
    this.#preloaded.clear();
  }

  /** @param {AbortSignal} signal */
  #setupCounter(signal) {
    const gallery = this.querySelector('[data-product-gallery]');
    const current = this.querySelector('[data-gallery-current]');
    if (!(gallery instanceof HTMLElement) || !current) return;
    let frame = 0;
    gallery.addEventListener(
      'scroll',
      () => {
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(() => {
          const width = gallery.clientWidth || 1;
          const index = Math.round(Math.abs(gallery.scrollLeft) / width) + 1;
          current.textContent = String(Math.min(index, gallery.children.length));
        });
      },
      { passive: true, signal }
    );
  }

  #handlePreloadIntent = (event) => {
    if (!(event.target instanceof Element)) return;
    const source = event.target.closest('[data-zoom-preload]');
    if (!source || !this.contains(source)) return;
    const dialogId = source.getAttribute('commandfor');
    const dialog = dialogId ? document.getElementById(dialogId) : null;
    if (dialog instanceof HTMLDialogElement) this.#preloadDialog(dialog);
  };

  /** @param {HTMLDialogElement} dialog */
  #preloadDialog(dialog) {
    if (!dialog.id || this.#preloaded.has(dialog.id)) return;
    const image = dialog.querySelector('img[data-zoom-image]');
    if (!(image instanceof HTMLImageElement)) return;

    this.#preloaded.add(dialog.id);
    image.loading = 'eager';
    image.fetchPriority = 'high';

    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.href = image.src;
    if (image.srcset) link.setAttribute('imagesrcset', image.srcset);
    if (image.sizes) link.setAttribute('imagesizes', image.sizes);
    document.head.append(link);
    this.#preloadLinks.push(link);

    if (typeof image.decode === 'function') image.decode().catch(() => {});
  }
}

if (!customElements.get('s-product-image-zoom')) {
  customElements.define('s-product-image-zoom', SProductImageZoom);
}
